import { createApi } from '@reduxjs/toolkit/query/react';
import { currentEnv, endpoints, getWithCredentialsOption } from '../../../helper/endpoints';
import { setExpiration, setUser, clearUser } from './authSlice';
import { axiosNoAuthBaseQuery } from '../axiosBaseQuery';

interface RefreshTokenResponse {
	token?: string;
	expiration: string;
}

interface ResetPasswordRequest {
	Token: string;
	Email: string;
	Password: string;
}

interface LoginRequest {
	username: string;
	password: string;
}
interface LoginResponse {
	token: string;
	requiresMFASetup?: boolean;
	requiresMFA?: boolean;
	requiresEmailMFA?: boolean;
	expiration?: string;
	admin?: boolean;
	id?: string;
}

interface SetupMFAResponse {
	qrCode?: string;
	message?: string;
}
interface LoginMFARequest {
	email: string;
	otpCode: string;
	type?: string | undefined;
}
interface LoginMFAResponse {
	token: string;
	expiration: string;
	admin: boolean;
	id: number;
}

interface VerifyMFARequest {
	otpCode: string;
	type?: string | undefined;
	email?: string | undefined;
}
interface VerifyMFAResponse {
	message: 'MFA enabled successfully!' | 'Email MFA successfully enabled.' | 'Invalid MFA token!';
}

interface SetupMFAParams {
	type?: string | undefined;
	email: string;
}

const withCredentialsOption = getWithCredentialsOption(currentEnv);

// Define a service using a base URL and expected endpoints
export const authApi = createApi({
	reducerPath: 'authApi',
	tagTypes: ['USER', 'REFRESH_TOKEN', 'SETUP_MFA'],
	baseQuery: axiosNoAuthBaseQuery({ baseUrl: '' }),
	endpoints: (builder) => ({
		getRefreshToken: builder.query<RefreshTokenResponse, string | null>({
			queryFn: async (expiration, _queryApi, _extraOptions, baseQuery) => {
				// const withCredentialsOption = getWithCredentialsOption(currentEnv);

				// const currentTime = new Date().getTime();
				// const isLocal = window.location.hostname === 'localhost';

				// console.log('EXPIRATION in queryFn', expiration);

				// if (expiration && (!withCredentialsOption || isLocal)) {
				// 	const expirationDate = expiration ? new Date(expiration) : new Date();
				// 	// console.log('expirationDate', expirationDate);
				// 	const expirationTime = expirationDate.getTime();
				// 	// console.log(
				// 	// 	'expiration time',
				// 	// 	expirationTime,
				// 	// 	expirationTime - currentTime > 10000
				// 	// );

				// 	// Return the existing token if it is not about to expire
				// 	if (expirationTime - currentTime > 10000) {
				// 		// console.log('Return existing expiration time', expiration);
				// 		return { data: { expiration } };
				// 	}
				// }
				// if (expiration) {
				// 	const expirationDate = expiration ? new Date(expiration) : new Date();
				// 	console.log('expirationDate', expirationDate);
				// 	const expirationTime = expirationDate.getTime();
				// 	console.log('expiration time', expirationTime, expirationTime - currentTime > 10000);

				// 	// Return the existing token if it is not about to expire
				// 	if (expirationTime - currentTime > 10000) {
				// 		console.log('Return existing expiration time', expiration);
				// 		return { data: { expiration } };
				// 	}
				// }

				try {
					const result = await baseQuery({
						url: endpoints.REFRESH_JWT_TOKEN,
						method: 'POST',
						data: {},
						withCredentials: withCredentialsOption,
					});
                    console.log('REFRESH TOKEN RESULT', result);
					if (result.error) {
						console.error('🚀 ~ REFRESH TOKEN ERROR', result.error);
						localStorage.clear();
						// Cookies.remove('token');
						return {
							error: result.error,
						};
					}

					// Store the new token
					// Cookies.set('token', result.data.token);
					return result;
				} catch (error: any) {
					console.log('🚀 ~ REFRESH TOKEN ERROR in queryFn', error);
					return error;
				}
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const res = await queryFulfilled;
					dispatch(setExpiration(res?.data?.expiration));
				} catch (error: any) {
					console.log('Error with refresh token', error);
				}
			},
		}),
		resetPassword: builder.mutation<any, ResetPasswordRequest>({
			query: (resetPasswordRequest) => ({
				url: endpoints.RESET_PASSWORD,
				method: 'POST',
				data: resetPasswordRequest,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				console.log('resetPassword password', response);
				return response;
			},
		}),
		forgotPassword: builder.mutation<any, string>({
			query: (email) => ({
				url: endpoints.FORGOT_PASSWORD,
				method: 'POST',
				data: { email },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				console.log('forgotPassword response', response);
				return response;
			},
		}),
		login: builder.mutation<LoginResponse, LoginRequest>({
			query: ({ username, password }) => ({
				url: endpoints.LOGIN,
				method: 'POST',
				data: { username, password },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				console.log('[login] mutation response', response);
				// Store the token in a secure cookie
				// Cookies.set('token', response.token, { secure: true });
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('[login] mutation error response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const response = await queryFulfilled;
					if ('id' in response?.data && 'token' in response?.data) {
						console.log('login onQueryStarted clearUser?', !('id' in response?.data));
						dispatch('id' in response?.data ? setUser(response?.data) : clearUser());
					}
				} catch (error: any) {
					console.log('Error [login] mutation awaiting queryFulfilled', error);
					dispatch(clearUser());
				}
			},
		}),
		logout: builder.mutation<any, undefined>({
			query: () => ({
				url: endpoints.LOGOUT,
				method: 'POST',
				// data: {  },
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
				withCredentials: withCredentialsOption,
			}),
			transformResponse: (response) => {
				console.log('[logout] mutation response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('[logout] mutation error response', response);
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
				} catch (error: any) {
					console.log('Error [logout] mutation awaiting queryFulfilled', error);
				} finally {
					dispatch(clearUser());
					// Cookies.remove('token');
					localStorage.clear();
				}
			},
		}),

		loginMFA: builder.mutation<LoginMFAResponse, LoginMFARequest>({
			query: ({ email, otpCode, type }) => ({
				url: type === 'email' ? endpoints.LOGIN_EMAIL_MFA : endpoints.LOGIN_MFA,
				method: 'POST',
				data: { Email: email, OtpCode: otpCode },
				withCredentials: withCredentialsOption,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
			}),
			transformResponse: (response) => {
				console.log('[loginMFA] mutation response', response);
				// Store the token in a secure cookie
				// Cookies.set('token', response.token, { secure: true });
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('[loginMFA] mutation error response', response);
				localStorage.removeItem('isAdmin');
				return response;
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					const response = await queryFulfilled;
					if ('id' in response?.data){ //} && 'token' in response?.data) {
						console.log(
							'loginMFA onQueryStarted clearUser?',
							!('id' in response?.data)
						);
						dispatch('id' in response?.data ? setUser(response?.data) : clearUser());
					}
				} catch (error: any) {
					console.log('Error [loginMFA] mutation awaiting queryFulfilled', error);
					dispatch(clearUser());
				}
			},
		}),
		setupMFA: builder.query<SetupMFAResponse, SetupMFAParams>({
			keepUnusedDataFor: 300,
			providesTags: (result, error, { email }) =>
				result?.qrCode || result?.message ? [{ type: 'SETUP_MFA', id: email }] : [],
			query: ({ type, email }) => ({
				url: type === 'email' ? endpoints.SETUP_EMAIL_MFA : endpoints.SETUP_MFA,
				method: 'POST',
				// data: { Email: email, OtpCode: otpCode },
				headers: {
					'Content-Type': 'application/json',
					// Accept: 'application/json, text/plain, */*',
				},
				withCredentials: withCredentialsOption,
			}),
			transformResponse: (response) => {
				console.log('[setupMFA] query response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('[setupMFA] query error response', response);
				return response;
			},
		}),
		verifyMFA: builder.mutation<VerifyMFAResponse, VerifyMFARequest>({
			query: ({ otpCode, type, email }) => ({
				url: type === 'email' ? endpoints.VERIFY_EMAIL_MFA : endpoints.VERIFY_MFA,
				method: 'POST',
				data: type === 'email' ? { otpCode, email } : `"${otpCode}"`,
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json, text/plain, */*',
				},
				withCredentials: withCredentialsOption,
			}),
			transformResponse: (response) => {
				console.log('[verifyMFA] mutation response', response);
				return response;
			},
			transformErrorResponse: (response) => {
				console.log('[verifyMFA] mutation error response', response);
				return response;
			},
			// onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
			// 	try {
			// 		const response = await queryFulfilled;
			//         dispatch(setUser(response?.data));
			// 	} catch (error: any) {
			// 		console.log('Error [verifyMFA] mutation awaiting queryFulfilled', error);
			// 	}
			// },
		}),
	}),
});

export const {
	useGetRefreshTokenQuery,
	useForgotPasswordMutation,
	useResetPasswordMutation,
	useLoginMutation,
	useLogoutMutation,
	useLoginMFAMutation,
	useSetupMFAQuery,
	useVerifyMFAMutation,
} = authApi;
